import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  ReactNode,
  useMemo,
} from 'react'

type ColumnInfo = {
  id: string
  ref: React.RefObject<HTMLTableCellElement>
}

type ColumnDimensions = {
  width: number
  height: number
  top: number
  left: number
}

interface FloatingDivProps extends ColumnDimensions {
  id: string
}

interface MarketShareTableTourWrapperProps {
  children: (props: {
    columnInfo: ColumnInfo[]
    tableRef: React.RefObject<HTMLTableElement>
  }) => ReactNode
}

const FloatingDiv: React.FC<FloatingDivProps> = ({
  id,
  top,
  left,
  width,
  height,
}) => (
  <div
    id={id}
    style={{
      position: 'absolute',
      top: `${top}px`,
      left: `${left}px`,
      width: `${width}px`,
      height: `${height}px`,
      pointerEvents: 'none',
    }}
  />
)

const MarketShareTableTourWrapper: React.FC<
  MarketShareTableTourWrapperProps
> = ({ children }) => {
  const tableRef = useRef<HTMLTableElement>(null)
  const [columnDimensions, setColumnDimensions] = useState<ColumnDimensions[]>(
    [],
  )

  // Create refs outside of useMemo
  const balancesRef = useRef<HTMLTableCellElement>(null)
  const marketShareRef = useRef<HTMLTableCellElement>(null)
  const interestRateRef = useRef<HTMLTableCellElement>(null)
  const secondaryLeverRef = useRef<HTMLTableCellElement>(null)
  const profitAfterTaxRef = useRef<HTMLTableCellElement>(null)

  const columnInfo = useMemo<ColumnInfo[]>(
    () => [
      { id: 'tour-balances', ref: balancesRef },
      { id: 'tour-market-share', ref: marketShareRef },
      { id: 'tour-interest-rate', ref: interestRateRef },
      { id: 'tour-secondary-lever', ref: secondaryLeverRef },
      { id: 'tour-profit-after-tax', ref: profitAfterTaxRef },
    ],
    [],
  ) // Empty dependency array because these refs never change

  const updateDimensions = useCallback(() => {
    if (!tableRef.current) return
    const tableRect = tableRef.current.getBoundingClientRect()
    const thirdRow = tableRef.current.querySelectorAll('tr')[2]
    const lastRowHeight = thirdRow ? thirdRow.getBoundingClientRect().height : 0
    const headerCells = tableRef.current.querySelectorAll('th')
    const secondColumnRect = headerCells[1]?.getBoundingClientRect()

    const newDimensions: ColumnDimensions[] = columnInfo.map(
      ({ ref }, index) => {
        if (!ref.current) return { width: 0, height: 0, top: 0, left: 0 }

        const columnRect = ref.current.getBoundingClientRect()
        return {
          width: columnRect.width,
          height:
            tableRect.height - lastRowHeight - columnRect.top + tableRect.top,
          top: columnRect.top - tableRect.top,
          left:
            index === 0 && secondColumnRect
              ? secondColumnRect.left - tableRect.left
              : columnRect.left - tableRect.left,
        }
      },
    )

    setColumnDimensions(newDimensions)
  }, [columnInfo])

  useEffect(() => {
    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [updateDimensions])

  return (
    <div className="w-full" style={{ position: 'relative' }}>
      {children({ columnInfo, tableRef })}
      {columnInfo.map(({ id }, index) => (
        <FloatingDiv key={id} id={id} {...columnDimensions[index]} />
      ))}
    </div>
  )
}

export default MarketShareTableTourWrapper
